<template>
  <v-app>
    <TopBar />
    <SideBar />
    <v-main>
      <!-- ROUTER VIEW -->
      <router-view :key="$route.fullPath" />
      <!-- ROUTER VIEW -->

      <SnackBackHandler
        v-for="(notification, index) in notifications"
        :key="notification.id"
        :notification="notification"
        :notificationId="notification.id"
        :notificationIndex="index"
        :pastNotificationInfoLength="pastNotificationInfoLength(index)"
      />
    </v-main>
    <!-- <v-app-bar app bottom dense short max-height="35px">
      <div class="d-flex align-center">
        <div class="mb-4">Updated at {{ new Date() }}</div>
      </div>
    </v-app-bar> -->
    <v-overlay
      :model-value="isPageLoading"
      :opacity="0.2"
      class="position-overlay"
    >
      <div class="absolute-center">
        <v-progress-circular
          :width="10"
          size="100"
          color="primary"
          indeterminate
        ></v-progress-circular>
      </div>
    </v-overlay>

    <v-navigation-drawer
      v-model="bottomDrawer"
      location="right"
      temporary
      :mobile-breakpoint="0"
    >
      <v-list nav density="compact">
        <v-list-item-group
          v-model="group"
          active-class="deep-purple--text text--accent-4"
        >
          <v-list-item>
            <v-list-item-title>
              <v-btn block @click="enableDarkMode"> Dark Mode </v-btn>
              <v-btn class="mt-2" block @click="disableDarkMode">
                Light Mode
              </v-btn>
            </v-list-item-title>
          </v-list-item>
        </v-list-item-group>
      </v-list>
    </v-navigation-drawer>
  </v-app>
</template>

<style>
html,
body,
.v-application {
  font-family: "Karla", sans-serif !important;
}

body {
  font-weight: 400;
  font-family: "Karla", sans-serif;
}

.v-application [class*="text-"] {
  font-family: "Karla", sans-serif !important;
}

.v-application .title,
.v-application .subtitle-1,
.v-application .subtitle-2,
.v-application .body-1,
.v-application .body-2,
.v-application .caption {
  font-family: "Karla", sans-serif !important;
}

.fixed-bar {
  position: sticky;
  position: -webkit-sticky; /* for Safari */
  top: 6em;
  z-index: 2;
}

.position-overlay {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.absolute-center {
  position: absolute !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  z-index: 9999 !important;
}

/* width */
::-webkit-scrollbar {
  width: 10px;
  opacity: 0.9;
}

/* Track */
::-webkit-scrollbar-track {
  border-radius: 5px;
  width: 10px;
}

::-webkit-scrollbar-track:horizontal {
  border-radius: 5px;
  width: 10px;
}

/* Handle */
::-webkit-scrollbar-thumb {
  background: #aaaaaa;
  border-radius: 0px;
  width: 10px;
}

/* Handle on hover */
::-webkit-scrollbar-thumb:hover {
  background: #25026a;
}

/* Global styles */
.v-data-table-header th {
  font-weight: bold !important;
  font-size: 1.05rem !important;
  color: rgba(0, 0, 0, 0.87) !important;
  padding: 0 16px !important;
  text-align: left !important;
}

.v-data-table-header th span {
  font-weight: bold !important;
}

.v-data-table {
  border-radius: 4px;
  overflow: hidden;
}

.v-data-table table {
  table-layout: auto !important;
  border-spacing: 0;
  display: table !important;
}

.v-data-table th,
.v-data-table td {
  display: table-cell !important;
}

.v-data-table tbody td {
  padding: 0 16px !important;
  text-align: left !important;
  font-size: 0.95rem !important;
}

/* Global styles for readonly and filled inputs */
:deep(.v-field--variant-filled) {
  background-color: #f5f5f5 !important;
}

:deep([readonly]) .v-field__overlay,
:deep(.v-field--disabled) .v-field__overlay {
  background-color: #f5f5f5 !important;
  opacity: 1 !important;
}

:deep(.v-field--variant-filled.v-field--disabled) {
  background-color: #f5f5f5 !important;
  opacity: 0.7 !important;
}
</style>

<script>
import { computed } from "vue";
import { useStore } from "vuex";
import TopBar from "./components/Global/TopBar.vue";
import SideBar from "./components/Global/SideBar.vue";
import SnackBackHandler from "./views/SnackBarError/SnackBackHandler.vue";
import { getDecodedTokenFromAuthHeader } from "./managrx/AutAndAuthServices";
import { useTheme } from "vuetify";

export default {
  name: "App",
  components: { TopBar, SideBar, SnackBackHandler },
  setup() {
    const store = useStore();
    const notifications = computed(() => store.state.notificationsQueue || []);
    const theme = useTheme();

    // Methods
    const enableDarkMode = () => {
      theme.global.name.value = "dark";
    };

    const disableDarkMode = () => {
      theme.global.name.value = "light";
    };

    const pastNotificationInfoLength = (index) => {
      if (index > 0) {
        let pastIndex = index - 1;
        return notifications.value?.[pastIndex]?.info
          ? notifications.value?.[pastIndex]?.info?.length
          : 1;
      } else {
        return 0;
      }
    };

    return {
      notifications,
      pastNotificationInfoLength,
      enableDarkMode,
      disableDarkMode,
      isPageLoading: computed(() => store.state.isPageLoading || false),
    };
  },
  mounted() {
    // Set default theme
    console.log("Starting App");
    this.$vuetify.theme.global.name = "light";

    // Add global error handler for ResizeObserver errors
    const originalConsoleError = console.error;
    console.error = function (msg, ...args) {
      // Don't log ResizeObserver loop errors
      if (
        typeof msg === "string" &&
        (msg.includes("ResizeObserver loop") ||
          msg.includes(
            "ResizeObserver loop completed with undelivered notifications",
          ))
      ) {
        return;
      }
      originalConsoleError(msg, ...args);
    };

    // Handle unhandled promise rejections that might relate to ResizeObserver
    window.addEventListener("unhandledrejection", (event) => {
      if (
        event.reason &&
        (String(event.reason).includes("ResizeObserver") ||
          (event.reason.message &&
            event.reason.message.includes("ResizeObserver")))
      ) {
        event.preventDefault();
        event.stopPropagation();
        console.log("Suppressed ResizeObserver error:", event.reason);
      }
    });

    // Add global event listener for route changes to handle ResizeObserver errors
    this.$router.beforeEach((to, from, next) => {
      // Temporarily disable animations before navigation to prevent ResizeObserver errors
      const style = document.createElement("style");
      style.id = "disable-animations";
      style.textContent =
        ".v-overlay, .v-menu, .v-select { animation-duration: 0.001s !important; transition-duration: 0.001s !important; }";
      document.head.appendChild(style);

      // Remove style after a short delay
      setTimeout(() => {
        const styleElement = document.getElementById("disable-animations");
        if (styleElement) {
          styleElement.remove();
        }
      }, 100);

      next();
    });

    // Add a MutationObserver to monitor DOM changes that might cause ResizeObserver errors
    const observer = new MutationObserver((mutations) => {
      // Check if any v-select or similar components were added or removed
      const hasRelevantChanges = mutations.some((mutation) => {
        return Array.from(mutation.addedNodes).some(
          (node) =>
            node.nodeType === 1 &&
            (node.classList?.contains("v-select") ||
              node.classList?.contains("v-menu") ||
              node.querySelector?.(".v-select, .v-menu")),
        );
      });

      if (hasRelevantChanges) {
        // Briefly disable animations to prevent ResizeObserver errors
        const style = document.createElement("style");
        style.id = "disable-animations-mutation";
        style.textContent =
          ".v-overlay, .v-menu, .v-select { animation-duration: 0.001s !important; transition-duration: 0.001s !important; }";
        document.head.appendChild(style);

        setTimeout(() => {
          const styleElement = document.getElementById(
            "disable-animations-mutation",
          );
          if (styleElement) {
            styleElement.remove();
          }
        }, 100);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: false,
      characterData: false,
    });

    // Decode and set user token
    let decodedToken = getDecodedTokenFromAuthHeader();
    if (decodedToken?.token) {
      this.$store.dispatch("setUserTokenInfo", decodedToken?.token);
    } else {
      // If no token is available from header, try to restore from localStorage
      const savedUserType = localStorage.getItem("userType");
      if (savedUserType) {
        console.log("Restoring user type from localStorage:", savedUserType);
        // Create a minimal token object with just the user type
        this.$store.dispatch("setUserTokenInfo", { utype: savedUserType });
      }
    }
  },
  updated() {
    // Token handling moved to mounted() hook
  },
  data: () => ({
    group: false,
    bottomDrawer: false,
  }),
};
</script>
